import { useState, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { Model, IJsonModel } from "flexlayout-react";
import {
  ActionIcon,
  Box,
  Group,
  Text,
  Stack,
  Title,
  Tooltip,
  useComputedColorScheme,
  useMantineColorScheme,
} from "@mantine/core";
import { IconSun, IconMoon, IconLayoutSidebarLeftCollapse, IconLayoutSidebarLeftExpand } from "@tabler/icons-react";
import {
  WorkspaceLayout,
  WidgetLibrary,
  loadLayoutFromLocalStorage,
  saveLayoutToLocalStorage,
  getLayoutStorageKey,
} from "@pulse-ui/layout-system";
import { getCloudWidgetRegistry } from "../../registry/widget-registry";
import { useFlexLayoutTheme } from "../../hooks/useFlexLayoutTheme";
import { DashboardDataProvider } from "../../features/dashboard/DashboardDataProvider";
import { selectIsAdmin, selectUserRoles } from "../../features/auth/authSelectors";
import "../../styles/flexlayout-overrides.css";

const STORAGE_KEY = getLayoutStorageKey("cloud-ui");

function createInitialModel(): IJsonModel {
  return {
    global: {
      tabEnableClose: true,
      tabEnableRename: false,
      tabSetEnableMaximize: true,
      tabSetEnableDrop: true,
      tabSetEnableDeleteWhenEmpty: true,
      tabSetEnableDrag: true,
      tabSetEnableDivide: true,
      borderEnableDrop: true,
      enableEdgeDock: true,
    },
    borders: [
      { type: "border", location: "left", children: [] },
      { type: "border", location: "right", children: [] },
      { type: "border", location: "bottom", children: [] },
    ],
    layout: {
      type: "row",
      weight: 100,
      children: [
        {
          type: "tabset",
          weight: 100,
          enableDrop: true,
          enableDrag: true,
          enableDivide: true,
          enableMaximize: true,
          children: [
            {
              type: "tab",
              name: "Welcome",
              component: "welcome",
              enableClose: true,
              config: {},
            },
          ],
        },
      ],
    },
  };
}

const Dashboard = () => {
  useFlexLayoutTheme();

  const { colorScheme, setColorScheme } = useMantineColorScheme();
  const computedColorScheme = useComputedColorScheme("dark");
  const isAdmin = useSelector(selectIsAdmin);
  const userRoles = useSelector(selectUserRoles);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [focusCategory, setFocusCategory] = useState<string | null>(null);

  const widgetRegistry = useMemo(() => getCloudWidgetRegistry(isAdmin, userRoles), [isAdmin, userRoles]);

  const enhancedWidgets = {
    ...widgetRegistry,
    welcome: {
      id: "welcome",
      name: "Welcome",
      description: "Welcome screen",
      category: "System",
      component: () => (
        <Box p="xl" h="100%">
          <Stack gap="md" align="center" justify="center" h="100%">
            <Title order={1} c="yellow.4">
              Dashboard
            </Title>
            <Text size="lg" c="dimmed">
              Widget-based trading workspace
            </Text>
            <Text size="sm" c="dimmed" mt="xl">
              Drag widgets from the sidebar to get started
            </Text>
          </Stack>
        </Box>
      ),
    },
  };

  const [model] = useState<IJsonModel>(() => {
    return loadLayoutFromLocalStorage(STORAGE_KEY) ?? createInitialModel();
  });

  const handleModelChange = useCallback((newModel: Model) => {
    saveLayoutToLocalStorage(STORAGE_KEY, newModel.toJson());
  }, []);

  return (
    <DashboardDataProvider>
      <Box
        style={{
          display: "grid",
          gridTemplateColumns: sidebarOpen ? "260px 1fr" : "40px 1fr",
          gridTemplateRows: "48px 1fr",
          gridTemplateAreas: `
            "sidebar header"
            "sidebar main"
          `,
          height: "100vh",
          marginLeft: "3em",
          overflow: "hidden",
          gap: "8px",
          padding: "8px",
          transition: "grid-template-columns 200ms ease",
          backgroundColor: colorScheme === "dark" ? "#000" : "#b6bdce",
        }}
      >
        {/* Top Bar */}
        <Box
          style={{
            gridArea: "header",
            backgroundColor: "var(--mantine-color-body)",
            borderRadius: "6px",
            border: "1px solid var(--mantine-color-default-border)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "0 var(--mantine-spacing-md)",
          }}
        >
          <Title order={4} c="dimmed">Dashboard</Title>
          <Group gap="xs">
            <ActionIcon
              onClick={() => setColorScheme(computedColorScheme === "dark" ? "light" : "dark")}
              variant="subtle"
              size="lg"
              aria-label="Toggle theme"
              color="var(--mantine-color-dimmed)"
              c="var(--mantine-color-dimmed)"
            >
              {computedColorScheme === "dark" ? <IconSun size={20} /> : <IconMoon size={20} />}
            </ActionIcon>
          </Group>
        </Box>

        {/* Widget Library Sidebar */}
        <Box
          style={{
            gridArea: "sidebar",
            backgroundColor: "var(--mantine-color-body)",
            borderRadius: "6px",
            border: "1px solid var(--mantine-color-default-border)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {/* Sidebar header — always visible */}
          <Box
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: sidebarOpen ? "space-between" : "center",
              padding: "0 8px 0 12px",
              height: 44,
              borderBottom: "1px solid var(--mantine-color-default-border)",
              flexShrink: 0,
            }}
          >
            {sidebarOpen && <Title order={4} c="dimmed">Widgets</Title>}
            <Tooltip label={sidebarOpen ? "Collapse sidebar" : "Expand sidebar"} position="right" withArrow>
              <ActionIcon
                onClick={() => {
                if (sidebarOpen) setFocusCategory(null);
                setSidebarOpen(!sidebarOpen);
              }}
                variant="subtle"
                size="sm"
                color="var(--mantine-color-dimmed)"
                c="var(--mantine-color-dimmed)"
                aria-label={sidebarOpen ? "Collapse sidebar" : "Expand sidebar"}
              >
                {sidebarOpen
                  ? <IconLayoutSidebarLeftCollapse size={18} />
                  : <IconLayoutSidebarLeftExpand size={18} />
                }
              </ActionIcon>
            </Tooltip>
          </Box>
          <Box style={{ flex: 1, overflow: "auto" }}>
            <WidgetLibrary
              widgetRegistry={widgetRegistry}
              collapsed={!sidebarOpen}
              focusCategory={focusCategory}
              onCategoryIconClick={(category) => {
                setFocusCategory(category);
                setSidebarOpen(true);
              }}
            />
          </Box>
        </Box>

        {/* Main Workspace */}
        <Box
          style={{
            gridArea: "main",
            backgroundColor: "var(--mantine-color-default)",
            borderRadius: "6px",
            border: "1px solid var(--mantine-color-default-border)",
            overflow: "hidden",
            position: "relative",
          }}
        >
          <WorkspaceLayout
            model={model}
            onModelChange={handleModelChange}
            widgetRegistry={enhancedWidgets}
          />
        </Box>
      </Box>
    </DashboardDataProvider>
  );
};

export default Dashboard;
